import { SeoMetadata } from '@seech/core-ng';
import {
  BRAND_KEYWORDS,
  mergeKeywordSets,
  normalizeText,
} from './seo-shared';

/**
 * Minimal shape a bundle needs to expose for metadata. Declared structurally so
 * this config does not depend on the generated API contract.
 */
export interface BundleSeoSource {
  bundleKey?: string | null;
  name?: string | null;
  hint?: string | null;
  description?: string | null;
  products?: { productName?: string | null }[] | null;
}

/** Keywords every bundle page shares. */
const BUNDLE_KEYWORDS = [
  'seech bundles',
  'app bundle',
  'software bundle pricing',
  'buy apps together',
  'save on apps',
];

const FALLBACK_TITLE = 'App Bundles | SEECH';
const FALLBACK_DESCRIPTION =
  'Buy SEECH apps together in curated bundles built around real outcomes, and save against buying each app separately.';

/**
 * Builds metadata for a single bundle page.
 *
 * Falls back to the catalog-level copy when a bundle has not resolved yet, so a
 * crawler never sees an empty title.
 *
 * @param bundle The bundle being displayed, if loaded.
 * @param fallbackImageUrl Brand image used for the social card; bundles have no
 * artwork of their own.
 */
export const buildBundleSeoMetadata = (
  bundle: BundleSeoSource | null | undefined,
  fallbackImageUrl: string
): SeoMetadata => {
  const name = normalizeText(bundle?.name);
  const title = name ? `${name} Bundle | SEECH` : FALLBACK_TITLE;

  const description =
    normalizeText(bundle?.description) ??
    normalizeText(bundle?.hint) ??
    FALLBACK_DESCRIPTION;

  const productNames = (bundle?.products ?? [])
    .map((product) => normalizeText(product?.productName))
    .filter((productName): productName is string => !!productName);

  const bundleKeywords = name ? [`${name} bundle`, name] : [];

  return {
    title,
    description,
    keywords: mergeKeywordSets(
      bundleKeywords,
      productNames,
      BUNDLE_KEYWORDS,
      BRAND_KEYWORDS
    ),
    imageUrl: fallbackImageUrl,
  };
};
